import { Link } from 'react-router-dom';

function Section({ title, subtitle, children }) {
  return (
    <section className="mt-14">
      <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {title}
      </h2>
      {subtitle ? (
        <p className="mt-3 max-w-4xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">{subtitle}</p>
      ) : null}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
      {children}
    </span>
  );
}

function BulletList({ items }) {
  return (
    <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
      {items.map((b) => (
        <li key={b} className="leading-6">
          {b}
        </li>
      ))}
    </ul>
  );
}

function InsightCard({ item }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl">
      <div className="flex flex-wrap items-center gap-2">
        <Pill>{item.category}</Pill>
        <span className="text-xs text-[var(--text-secondary)]">|</span>
        <span className="text-xs text-[var(--text-secondary)]">{item.readTime}</span>
        <span className="text-xs text-[var(--text-secondary)]">|</span>
        <span className="text-xs text-[var(--text-secondary)]">{item.date}</span>
      </div>
      <div className="mt-4 text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
        {item.title}
      </div>
      <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">{item.summary}</p>
      <div className="mt-5 text-sm font-semibold text-[var(--text-primary)]">Key Topics</div>
      <BulletList items={item.topics} />
      <div className="mt-5 text-sm">
        <span className="font-semibold text-[var(--text-primary)]">Target Audience:</span>{" "}
        <span className="text-[var(--text-secondary)]">{item.audience}</span>
      </div>
    </div>
  );
}

export default function Insights() {
  const categories = [
    { label: "TRENDING", sub: "Hot Topics" },
    { label: "THREAT INTEL", sub: "Attack Analysis" },
    { label: "BEST PRACTICES", sub: "How-To Guides" },
    { label: "INDUSTRY", sub: "Sector Focus" },
    { label: "REGULATORY", sub: "Compliance" },
    { label: "TECHNOLOGY", sub: "Deep Dives" },
  ];

  const featured = [
    {
      category: "TRENDING",
      readTime: "12 min read",
      date: "December 2024",
      title: "The Rise of Identity-First Security: Why IAM is Now the New Perimeter",
      summary:
        "As traditional network perimeters dissolve in the era of cloud and remote work, identity has emerged as the primary security control plane. This analysis explores why leading organizations are shifting to identity-first security architectures and what it means for your security strategy.",
      topics: [
        "Why network-centric security models are failing in modern environments",
        "The identity-first security framework: principles and implementation",
        "Case studies: Organizations that successfully pivoted to identity-centric security",
        "Key technologies enabling identity-first security (ITDR, CIEM, Zero Trust)",
        "Roadmap for transitioning from perimeter to identity-first security",
      ],
      audience: "Security Leaders, CISOs, Enterprise Architects",
    },
    {
      category: "THREAT INTEL",
      readTime: "15 min read",
      date: "December 2024",
      title: "2024 Identity Threat Landscape: Credential Attacks, AI-Powered Threats, and Emerging Risks",
      summary:
        "A deep dive into the evolving identity threat landscape based on analysis of 500+ incidents across Indian enterprises. Covers the latest attack techniques, threat actor tactics, and defensive strategies for identity infrastructure.",
      topics: [
        "Credential stuffing and password spray attacks: 340% increase in sophistication",
        "MFA fatigue and push notification attacks: bypassing second factors",
        "AI-generated deepfakes in identity verification fraud",
        "Supply chain identity attacks: compromising federated trust",
        "Insider threats leveraging legitimate credentials",
        "Defensive recommendations and detection strategies",
      ],
      audience: "SOC Teams, Threat Analysts, Identity Architects",
    },
    {
      category: "REGULATORY",
      readTime: "18 min read",
      date: "November 2024",
      title: "DPDP Act 2023: Identity Management Implications and Compliance Roadmap",
      summary:
        "India's Digital Personal Data Protection Act introduces significant requirements for identity management. This guide breaks down identity-related provisions and provides a practical compliance roadmap.",
      topics: [
        "Key DPDP provisions affecting identity management",
        "Consent management and purpose limitation for identity data",
        "Data principal rights: access, correction, erasure implications",
        "Cross-border transfer restrictions and localization requirements",
        "Breach notification requirements for identity incidents",
        "Step-by-step compliance roadmap with timeline",
        "Technology controls mapping to DPDP requirements",
      ],
      audience: "Compliance Officers, DPOs, Legal Teams, CISOs",
    },
  ];

  const threatIntel = [
    {
      category: "THREAT INTEL",
      readTime: "10 min read",
      date: "December 2024",
      title: "Anatomy of an AiTM Attack: How Adversary-in-the-Middle Phishing Bypasses MFA",
      summary:
        "AiTM attacks have emerged as a significant threat capable of bypassing traditional MFA. This technical analysis dissects real-world campaigns targeting Indian financial institutions.",
      topics: [
        "Technical mechanics of AiTM phishing infrastructure",
        "Session token hijacking and cookie theft techniques",
        "Real-world case study: Banking sector AiTM campaign",
        "Detection strategies using identity telemetry",
        "Mitigation: phishing-resistant MFA (FIDO2, certificate-based)",
      ],
      audience: "Security Engineers, SOC Analysts, Identity Architects",
    },
    {
      category: "THREAT INTEL",
      readTime: "14 min read",
      date: "November 2024",
      title: "Active Directory Under Siege: Detecting and Preventing AD-Specific Attacks",
      summary:
        "Active Directory remains the backbone of enterprise identity and a prime target for attackers. This guide covers modern AD attack techniques and detection strategies.",
      topics: [
        "Kerberoasting and AS-REP roasting: extracting service account credentials",
        "Golden Ticket and Silver Ticket attacks explained",
        "DCSync attacks: replicating the entire AD database",
        "AD Certificate Services (ADCS) exploitation",
        "Detection using Windows event logs and identity analytics",
        "Hardening recommendations and monitoring requirements",
      ],
      audience: "AD Administrators, Security Teams, Identity Engineers",
    },
    {
      category: "THREAT INTEL",
      readTime: "8 min read",
      date: "October 2024",
      title: "OAuth Token Theft: The Silent Identity Compromise",
      summary:
        "OAuth and OIDC token theft is increasingly used to maintain persistent access without triggering authentication alerts. Learn how attackers exploit token vulnerabilities and how to protect OAuth infrastructure.",
      topics: [
        "Token theft techniques: memory scraping, token replay, consent phishing",
        "Detecting anomalous OAuth token usage patterns",
        "Implementing token binding and continuous access evaluation",
        "Reducing token lifetime and implementing just-in-time tokens",
        "Monitoring strategies for OAuth/OIDC infrastructure",
      ],
      audience: "Application Security, DevSecOps, Identity Architects",
    },
    {
      category: "THREAT INTEL",
      readTime: "7 min read",
      date: "October 2024",
      title: "SIM Swap Fraud: Protecting High-Value Transactions from Telecom-Based Attacks",
      summary:
        "SIM swap attacks enable adversaries to intercept OTPs and take over accounts. This brief examines the threat and countermeasures.",
      topics: [
        "SIM swap attack mechanics and social engineering tactics",
        "Impact on SMS-based authentication in banking",
        "Telecom API integration for SIM swap detection",
        "Alternative authentication methods reducing SIM dependency",
        "RBI guidelines and industry response",
      ],
      audience: "Banking Security, Product Managers, Authentication Architects",
    },
  ];

  const bestPractices = [
    {
      category: "BEST PRACTICES",
      readTime: "20 min read",
      date: "December 2024",
      title: "Zero Trust Identity Architecture: A Practical Implementation Guide",
      summary:
        "A practical guide to implementing identity-centric Zero Trust architecture with reference patterns, selection criteria, and phased approach.",
      topics: [
        "Zero Trust principles applied to identity",
        "Reference architecture: identity as the control plane",
        "Continuous verification vs. session-based trust",
        "Device trust and identity binding",
        "Microsegmentation based on identity context",
        "Technology stack: IdP, ITDR, CIEM, PAM integration",
        "Implementation phases and quick wins",
      ],
      audience: "Enterprise Architects, Security Architects, CISOs",
    },
    {
      category: "BEST PRACTICES",
      readTime: "16 min read",
      date: "November 2024",
      title: "Passwordless Authentication: Strategy, Technology, and Deployment Playbook",
      summary:
        "A comprehensive guide covering passwordless technologies, selection criteria, and deployment strategies for enterprises.",
      topics: [
        "The case for passwordless: security and UX benefits",
        "Technology landscape: FIDO2, passkeys, biometrics, magic links",
        "Platform support matrix (Windows, macOS, iOS, Android)",
        "Deployment strategies: greenfield vs. brownfield",
        "User enrollment and recovery flows",
        "Handling edge cases: shared devices, kiosks, legacy apps",
        "Success metrics and adoption tracking",
      ],
      audience: "Identity Architects, Product Managers, UX Teams",
    },
    {
      category: "BEST PRACTICES",
      readTime: "14 min read",
      date: "November 2024",
      title: "Privileged Access Management: From Vault to Zero Standing Privilege",
      summary:
        "Modern PAM goes beyond vaulting to just-in-time access and zero standing privileges. This guide covers next-gen PAM evolution and implementation.",
      topics: [
        "PAM maturity model: vault → session → JIT → zero standing",
        "Just-in-time privileged access implementation",
        "Eliminating standing admin rights",
        "Cloud PAM across AWS, Azure, GCP",
        "DevOps and CI/CD pipeline privileged access",
        "OT/ICS privileged access considerations",
        "PAM metrics and continuous improvement",
      ],
      audience: "Security Operations, PAM Administrators, Cloud Architects",
    },
    {
      category: "BEST PRACTICES",
      readTime: "10 min read",
      date: "October 2024",
      title: "Access Certification Done Right: Moving from Checkbox to Risk-Based Reviews",
      summary:
        "Transform access certification from a compliance checkbox into a real risk-reduction capability with risk-based campaigns and automation.",
      topics: [
        "Why traditional access reviews fail",
        "Risk-based certification: focusing on what matters",
        "AI/ML for intelligent review recommendations",
        "Micro-certifications vs. annual campaigns",
        "Manager vs. application owner certifications",
        "Measuring certification effectiveness",
        "Automating low-risk certifications",
      ],
      audience: "IGA Administrators, Compliance Teams, Identity Managers",
    },
    {
      category: "BEST PRACTICES",
      readTime: "9 min read",
      date: "October 2024",
      title: "Service Account Management: The Forgotten Identity Risk",
      summary:
        "Service accounts often outnumber human users and are frequently over-privileged and unmonitored. This guide addresses service account sprawl.",
      topics: [
        "The service account sprawl problem: scope and risk",
        "Discovery techniques for unknown service accounts",
        "Service account lifecycle management",
        "Workload identity federation: eliminating static credentials",
        "Secrets management integration",
        "Monitoring service account behavior",
        "Migration path from legacy service accounts",
      ],
      audience: "Infrastructure Teams, DevOps, Identity Architects",
    },
  ];

  const industryFocus = [
    {
      category: "INDUSTRY",
      readTime: "15 min read",
      date: "December 2024",
      title: "Banking Identity Security: Meeting RBI Cybersecurity Framework Requirements",
      summary:
        "Implement identity controls that satisfy RBI cybersecurity framework requirements for banks and NBFCs with a clear compliance roadmap.",
      topics: [
        "RBI framework identity control requirements breakdown",
        "Privileged access management mandates",
        "Customer authentication and fraud prevention",
        "Access governance and certification requirements",
        "Audit logging and monitoring specifications",
        "Implementation timeline and compliance roadmap",
        "Sample control evidence for RBI audits",
      ],
      audience: "Banking CISOs, Compliance Officers, IT Risk Managers",
    },
    {
      category: "INDUSTRY",
      readTime: "12 min read",
      date: "November 2024",
      title: "Healthcare Identity: Securing Patient Data While Enabling Care Delivery",
      summary:
        "Healthcare organizations must protect sensitive patient data while enabling fast clinical access. This guide addresses healthcare-specific patterns.",
      topics: [
        "Shared workstations and emergency access",
        "ABDM integration and patient identity management",
        "Tap-and-go and proximity authentication",
        "Break-glass access for emergencies",
        "Medical device identity and IoMT security",
        "HIPAA/DISHA compliance requirements",
      ],
      audience: "Healthcare CIOs, Clinical IT Teams, Compliance Officers",
    },
    {
      category: "INDUSTRY",
      readTime: "14 min read",
      date: "November 2024",
      title: "OT/ICS Identity: Bridging IT Security and Industrial Operations",
      summary:
        "Industrial environments require identity approaches that balance security with operational reality. Covers identity management for OT/ICS.",
      topics: [
        "IT vs. OT identity: understanding the differences",
        "OT-specific authentication challenges",
        "Integrating IT identity with OT systems",
        "PAM for SCADA and industrial control systems",
        "Vendor and contractor remote access",
        "IEC 62443 identity requirements",
        "Converged IT/OT identity architecture",
      ],
      audience: "OT Security, Industrial Cybersecurity, Plant Managers",
    },
    {
      category: "INDUSTRY",
      readTime: "13 min read",
      date: "October 2024",
      title: "Government Digital Identity: Building Citizen Trust Through Secure Access",
      summary:
        "Deliver convenient citizen services while maintaining high security standards with proven e-governance identity patterns.",
      topics: [
        "Citizen identity verification using Aadhaar/DigiLocker",
        "Government employee identity lifecycle",
        "Inter-department federation patterns",
        "Inclusive authentication for diverse populations",
        "Physical-logical access integration",
        "MeitY guidelines and GIGW compliance",
        "Case studies: successful e-governance identity implementations",
      ],
      audience: "Government CIOs, e-Governance Teams, NIC Officers",
    },
  ];

  const regulatory = [
    {
      category: "REGULATORY",
      readTime: "11 min read",
      date: "December 2024",
      title: "SEBI CSCRF 2024: Identity and Access Control Requirements for Market Infrastructure",
      summary:
        "SEBI's updated CSCRF introduces enhanced identity requirements for market infrastructure. This guide breaks down requirements and timelines.",
      topics: [
        "CSCRF identity control requirements analysis",
        "Privileged access management mandates",
        "Multi-factor authentication requirements",
        "Third-party access controls",
        "Audit trail and logging specifications",
        "Implementation timeline for different entity categories",
        "Gap assessment template for CSCRF compliance",
      ],
      audience: "Capital Market CISOs, Compliance Officers, IT Auditors",
    },
    {
      category: "REGULATORY",
      readTime: "10 min read",
      date: "November 2024",
      title: "PCI-DSS 4.0: What's New for Identity and Authentication",
      summary:
        "PCI-DSS 4.0 introduces significant changes to authentication requirements. Learn what payment card handlers need to know.",
      topics: [
        "Key PCI-DSS 4.0 changes affecting identity",
        "New MFA requirements (Requirement 8)",
        "Service account and system account requirements",
        "Password complexity and rotation updates",
        "Phishing-resistant authentication recommendations",
        "Timeline: what's required by March 2025",
        "Compliance checklist for identity controls",
      ],
      audience: "PCI QSAs, Payment Security Teams, Compliance Managers",
    },
    {
      category: "REGULATORY",
      readTime: "8 min read",
      date: "October 2024",
      title: "CERT-In Directions 2022: Identity Logging and Reporting Requirements",
      summary:
        "CERT-In directions mandate specific logging and reporting requirements that directly impact identity infrastructure. Understand your obligations.",
      topics: [
        "CERT-In logging requirements for identity events",
        "6-hour incident reporting: identity incident scenarios",
        "Log retention requirements (180 days/5 years)",
        "VPN and remote access logging specifics",
        "Clock synchronization requirements",
        "Technical implementation guidance",
        "Sample log formats and event categories",
      ],
      audience: "Security Operations, Compliance Teams, IT Infrastructure",
    },
    {
      category: "REGULATORY",
      readTime: "12 min read",
      date: "October 2024",
      title: "Cross-Border Identity: Navigating GDPR, DPDP, and Global Privacy Requirements",
      summary:
        "Multinational organizations face complex identity compliance requirements. This guide helps navigate privacy regulations affecting identity management.",
      topics: [
        "Mapping identity data flows across borders",
        "GDPR vs. DPDP: key differences for identity",
        "Data localization requirements by country",
        "Standard contractual clauses for identity data",
        "Binding corporate rules for global identity",
        "Technical controls for compliance",
        "Audit and accountability documentation",
      ],
      audience: "Global Privacy Officers, Legal Teams, International CISOs",
    },
  ];

  const technology = [
    {
      category: "TECHNOLOGY",
      readTime: "14 min read",
      date: "December 2024",
      title: "ITDR Explained: Identity Threat Detection and Response Architecture",
      summary:
        "ITDR has emerged as a critical security category. This deep dive covers architecture, capabilities, and implementation considerations.",
      topics: [
        "What is ITDR and why it matters now",
        "ITDR vs. traditional IAM security",
        "Core ITDR capabilities: detection, investigation, response",
        "Identity attack detection techniques",
        "Integration with SIEM, SOAR, and XDR",
        "Vendor landscape and selection criteria",
        "Building vs. buying ITDR capabilities",
      ],
      audience: "Security Architects, SOC Leaders, Identity Engineers",
    },
    {
      category: "TECHNOLOGY",
      readTime: "16 min read",
      date: "November 2024",
      title: "Decentralized Identity and Verifiable Credentials: Beyond the Hype",
      summary:
        "Decentralized identity promises to change digital identity. This analysis cuts through hype to examine practical applications and limitations.",
      topics: [
        "Decentralized identity fundamentals: DIDs, VCs, wallets",
        "Use cases with real-world traction",
        "Enterprise applications: employee credentials, supply chain",
        "Government applications: digital identity documents",
        "Technical challenges and limitations",
        "Interoperability and standards landscape",
        "Realistic timeline for enterprise adoption",
      ],
      audience: "Innovation Teams, Enterprise Architects, Identity Strategists",
    },
    {
      category: "TECHNOLOGY",
      readTime: "13 min read",
      date: "November 2024",
      title: "AI and Machine Learning in Identity: Current State and Future Potential",
      summary:
        "AI/ML is transforming identity management from risk scoring to behavioral analytics. This analysis covers current applications and emerging possibilities.",
      topics: [
        "AI/ML applications in identity today",
        "Behavioral biometrics and continuous authentication",
        "AI-powered access recommendations and certifications",
        "Identity risk scoring models",
        "Fraud detection using ML",
        "Generative AI threats to identity (deepfakes)",
        "Future: autonomous identity governance",
      ],
      audience: "Identity Product Managers, Data Scientists, Innovation Teams",
    },
    {
      category: "TECHNOLOGY",
      readTime: "11 min read",
      date: "October 2024",
      title: "CIEM Deep Dive: Managing Cloud Infrastructure Entitlements at Scale",
      summary:
        "CIEM addresses the challenge of managing identities and permissions across multi-cloud environments. Learn core capabilities and best practices.",
      topics: [
        "The cloud entitlement sprawl problem",
        "CIEM capabilities: discovery, analysis, remediation",
        "Multi-cloud permission models: AWS IAM, Azure RBAC, GCP IAM",
        "Least privilege enforcement in cloud",
        "Integration with IGA and PAM",
        "Vendor comparison and selection criteria",
        "Implementation best practices",
      ],
      audience: "Cloud Security, DevSecOps, Cloud Architects",
    },
    {
      category: "TECHNOLOGY",
      readTime: "10 min read",
      date: "October 2024",
      title: "Passkeys Deep Dive: The Technical Foundation for Passwordless Future",
      summary:
        "Passkeys represent the most significant authentication advancement in decades. This deep dive covers how passkeys work and implementation considerations.",
      topics: [
        "Passkey architecture: WebAuthn, CTAP2, syncing",
        "How passkeys prevent phishing",
        "Platform passkeys vs. roaming authenticators",
        "Cross-device authentication flows",
        "Account recovery without passwords",
        "Implementation: relying party considerations",
        "Passkey adoption metrics and momentum",
      ],
      audience: "Authentication Engineers, Security Architects, Developers",
    },
  ];

  const quickTakes = [
    {
      title: "Microsoft Entra ID Governance GA: What It Means for Existing Azure AD Customers",
      meta: "3 min  |  Dec 2024",
      desc: "Analysis of Microsoft's converged identity governance offering and migration considerations.",
    },
    {
      title: "Okta Breach Post-Mortem: Lessons for Identity Provider Security",
      meta: "4 min  |  Dec 2024",
      desc: "Key takeaways from recent identity provider security incidents and defensive recommendations.",
    },
    {
      title: "Google Passkeys Milestone: 1 Billion Authentications",
      meta: "2 min  |  Nov 2024",
      desc: "What Google's passkey adoption numbers mean for enterprise passwordless strategies.",
    },
    {
      title: "FIDO Alliance Updates: New Specs for Enterprise Passkey Management",
      meta: "3 min  |  Nov 2024",
      desc: "Overview of new FIDO specifications enabling enterprise passkey administration.",
    },
    {
      title: "SailPoint IPO: What It Signals for the IGA Market",
      meta: "3 min  |  Nov 2024",
      desc: "Market analysis following SailPoint's return to public markets.",
    },
    {
      title: "RBI Digital Lending Guidelines: Identity Verification Updates",
      meta: "4 min  |  Oct 2024",
      desc: "New RBI requirements for borrower identity verification in digital lending.",
    },
    {
      title: "CrowdStrike Enters ITDR: Competitive Landscape Shift",
      meta: "3 min  |  Oct 2024",
      desc: "Analysis of CrowdStrike's identity threat detection capabilities and market positioning.",
    },
    {
      title: "India Stack 2.0: Account Aggregator Identity Implications",
      meta: "4 min  |  Oct 2024",
      desc: "How Account Aggregator framework affects consent-based data sharing and identity.",
    },
  ];

  const reports = [
    {
      title: "Identity Security Maturity Report: India 2024",
      meta: "Annual Survey Report  |  40 pages",
      desc:
        "Comprehensive survey of identity security practices across 200+ Indian enterprises. Includes maturity benchmarks, investment trends, and technology adoption patterns.",
      includes:
        "Includes: Maturity scores by industry vertical | Investment priorities for 2025 | Technology adoption curves | Challenge rankings | Recommendations by maturity level",
    },
    {
      title: "State of Privileged Access Management in Indian Enterprises",
      meta: "Research Report  |  32 pages",
      desc:
        "Deep dive into PAM adoption, challenges, and best practices based on interviews with 50 security leaders and analysis of 100 implementations.",
      includes:
        "Includes: PAM maturity distribution | Top implementation challenges | Cloud PAM adoption trends | Vendor satisfaction ratings | ROI benchmarks",
    },
    {
      title: "Customer Identity Trends: BFSI Edition 2024",
      meta: "Industry Report  |  28 pages",
      desc:
        "Analysis of Customer identity management trends in Indian banking and financial services, covering authentication preferences, fraud patterns, and regulatory impacts.",
      includes:
        "Includes: Authentication method preferences | Fraud attempt patterns | eKYC adoption rates | Customer friction analysis | Regulatory impact assessment",
    },
    {
      title: "Identity Attack Surface: India Threat Report 2024",
      meta: "Threat Intelligence Report  |  35 pages",
      desc:
        "Analysis of identity-focused attacks targeting Indian organizations, based on incident data from 500+ security events.",
      includes:
        "Includes: Attack type distribution | Industry targeting analysis | Attack success factors | Detection gap analysis | Defensive recommendations",
    },
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-[var(--gradient-hero)] p-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_28%,rgba(6,182,212,0.14),transparent_55%),radial-gradient(circle_at_72%_22%,rgba(37,99,235,0.18),transparent_45%)]" />
        <div className="relative">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">
            Centre of Excellence
          </div>
          <h1
            className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Identity Security Insights
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
            Trends, Threats, and Best Practices in Identity Management
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="mailto:insights@identity-coe.com"
              className="inline-flex items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20"
            >
              Subscribe
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.02] px-5 py-3 text-sm font-semibold text-[var(--text-primary)] hover:bg-white/[0.05]"
            >
              Contact research team
            </Link>
          </div>
        </div>
      </div>

      <Section title="Content Categories">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((c) => (
            <div key={c.label} className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 backdrop-blur-xl">
              <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{c.label}</div>
              <div className="mt-2 text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
                {c.sub}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Featured Insights">
        <div className="grid gap-4 lg:grid-cols-3">
          {featured.map((item) => (
            <InsightCard key={item.title} item={item} />
          ))}
        </div>
      </Section>

      <Section title="Threat Intelligence">
        <div className="grid gap-4 lg:grid-cols-2">
          {threatIntel.map((item) => (
            <InsightCard key={item.title} item={item} />
          ))}
        </div>
      </Section>

      <Section title="Best Practices & Guides">
        <div className="grid gap-4 lg:grid-cols-2">
          {bestPractices.map((item) => (
            <InsightCard key={item.title} item={item} />
          ))}
        </div>
      </Section>

      <Section title="Industry Focus">
        <div className="grid gap-4 lg:grid-cols-2">
          {industryFocus.map((item) => (
            <InsightCard key={item.title} item={item} />
          ))}
        </div>
      </Section>

      <Section title="Regulatory & Compliance">
        <div className="grid gap-4 lg:grid-cols-2">
          {regulatory.map((item) => (
            <InsightCard key={item.title} item={item} />
          ))}
        </div>
      </Section>

      <Section title="Technology Deep Dives">
        <div className="grid gap-4 lg:grid-cols-2">
          {technology.map((item) => (
            <InsightCard key={item.title} item={item} />
          ))}
        </div>
      </Section>

      <Section title="Quick Takes" subtitle="Short-form insights on emerging trends and news.">
        <div className="grid gap-3">
          {quickTakes.map((q) => (
            <div key={q.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-semibold text-[var(--text-primary)]">⚡ {q.title}</div>
                <div className="text-xs text-[var(--text-secondary)]">{q.meta}</div>
              </div>
              <div className="mt-2 text-sm text-[var(--text-secondary)]">{q.desc}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Research & Reports">
        <div className="grid gap-4 lg:grid-cols-2">
          {reports.map((r) => (
            <div key={r.title} className="rounded-3xl border border-white/10 bg-[var(--gradient-card)] p-7 backdrop-blur-xl">
              <div className="flex flex-wrap items-center gap-2">
                <Pill>REPORT</Pill>
                <span className="text-xs text-[var(--text-secondary)]">|</span>
                <span className="text-xs text-[var(--text-secondary)]">{r.meta}</span>
              </div>
              <div className="mt-4 text-lg font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
                📊 {r.title}
              </div>
              <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">{r.desc}</p>
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 text-sm text-[var(--text-secondary)]">
                {r.includes}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <div className="mt-14 rounded-3xl border border-white/10 bg-white/[0.03] p-8 backdrop-blur-xl sm:p-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              Subscribe to Identity Insights
            </h2>
            <p className="mt-4 text-sm leading-7 text-[var(--text-secondary)] sm:text-base">
              Get the latest identity security insights delivered to your inbox. Weekly digest of trends, threats, and best practices.
            </p>
            <div className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">Weekly Digest: Curated top stories and new publications</div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">Threat Alerts: Breaking identity threat intelligence</div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">Regulatory Updates: Compliance changes affecting identity</div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">Event Invitations: Webinars, workshops, and conferences</div>
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[var(--gradient-card)] p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Subscribe</div>
            <div className="mt-3 text-xl font-semibold tracking-tight" style={{ fontFamily: "var(--font-display)" }}>
              insights@identity-coe.com
            </div>
            <p className="mt-3 text-sm leading-7 text-[var(--text-secondary)]">
              Have a topic you'd like us to cover? Contact our research team.
            </p>
            <Link
              to="mailto:insights@identity-coe.com"
              className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-[var(--accent-trust)] px-5 py-3 text-sm font-semibold text-white"
            >
              Subscribe
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
